import { parseDocument, stringify, type Document, type ParsedNode } from 'yaml';

import type { CheckboxOption, IssueField, IssueForm, RepositorySettings } from '../domain/types';
import { createUid, normalizeStringList } from '../domain/utils';

const DEFAULT_MAX_YAML_BYTES = 1_048_576;
const MAX_STRUCTURE_DEPTH = 32;
const MAX_STRUCTURE_NODES = 20_000;

type UnknownRecord = Record<string, unknown>;
type YamlDocument = Document.Parsed<ParsedNode>;

export interface ParseIssueFormOptions {
  filename?: string;
  maxBytes?: number;
}

export class IssueFormParseError extends Error {
  readonly line: number | undefined;
  readonly column: number | undefined;

  constructor(message: string, line?: number, column?: number) {
    super(message);
    this.name = 'IssueFormParseError';
    this.line = line;
    this.column = column;
  }
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describeType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'list';
  return typeof value;
}

function parseError(message: string): never {
  throw new IssueFormParseError(message);
}

function expectRecord(value: unknown, path: string): UnknownRecord {
  if (!isRecord(value)) parseError(`${path} must be an object.`);
  return value;
}

function requiredString(record: UnknownRecord, key: string, path: string): string {
  const value = record[key];
  if (typeof value !== 'string') {
    parseError(`${path}.${key} must be a string, received ${describeType(value)}.`);
  }
  return value;
}

function editableString(record: UnknownRecord, key: string, path: string): string {
  const value = record[key];
  if (value === undefined || value === null) return '';
  if (typeof value !== 'string') {
    parseError(`${path}.${key} must be a string, received ${describeType(value)}.`);
  }
  return value;
}

function optionalBoolean(
  record: UnknownRecord,
  key: string,
  path: string,
  fallback = false,
): boolean {
  const value = record[key];
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'boolean') {
    parseError(`${path}.${key} must be a boolean, received ${describeType(value)}.`);
  }
  return value;
}

function optionalStringList(record: UnknownRecord, key: string, path: string): string[] {
  const value = record[key];
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) parseError(`${path}.${key} must be a list of strings.`);
  value.forEach((entry, index) => {
    if (typeof entry !== 'string') {
      parseError(`${path}.${key}[${index}] must be a string.`);
    }
  });
  return normalizeStringList(value as string[]);
}

function parseValidations(record: UnknownRecord, path: string): { required: boolean } {
  const value = record.validations;
  if (value === undefined || value === null) return { required: false };
  const validations = expectRecord(value, `${path}.validations`);
  return {
    required: optionalBoolean(validations, 'required', `${path}.validations`),
  };
}

function parseCheckboxOption(value: unknown, path: string): CheckboxOption {
  const option = expectRecord(value, path);
  return {
    uid: createUid('option'),
    label: editableString(option, 'label', path),
    required: optionalBoolean(option, 'required', path),
  };
}

function parseField(value: unknown, index: number): IssueField {
  const path = `body[${index}]`;
  const field = expectRecord(value, path);
  const type = field.type;
  if (typeof type !== 'string') parseError(`${path}.type must be a string.`);
  if (
    type !== 'markdown' &&
    type !== 'input' &&
    type !== 'textarea' &&
    type !== 'dropdown' &&
    type !== 'checkboxes'
  ) {
    parseError(`Unknown field type "${type}" at ${path}.type.`);
  }

  const attributes = expectRecord(field.attributes, `${path}.attributes`);
  if (type === 'markdown') {
    return {
      uid: createUid('field'),
      type,
      attributes: { value: editableString(attributes, 'value', `${path}.attributes`) },
    };
  }

  const id = editableString(field, 'id', path);
  if (type === 'input') {
    return {
      uid: createUid('field'),
      type,
      id,
      attributes: {
        label: editableString(attributes, 'label', `${path}.attributes`),
        description: editableString(attributes, 'description', `${path}.attributes`),
        placeholder: editableString(attributes, 'placeholder', `${path}.attributes`),
        value: editableString(attributes, 'value', `${path}.attributes`),
      },
      validations: parseValidations(field, path),
    };
  }

  if (type === 'textarea') {
    return {
      uid: createUid('field'),
      type,
      id,
      attributes: {
        label: editableString(attributes, 'label', `${path}.attributes`),
        description: editableString(attributes, 'description', `${path}.attributes`),
        placeholder: editableString(attributes, 'placeholder', `${path}.attributes`),
        value: editableString(attributes, 'value', `${path}.attributes`),
        render: editableString(attributes, 'render', `${path}.attributes`),
      },
      validations: parseValidations(field, path),
    };
  }

  if (type === 'dropdown') {
    const rawOptions = attributes.options;
    if (rawOptions !== undefined && !Array.isArray(rawOptions)) {
      parseError(`${path}.attributes.options must be a list of strings.`);
    }
    const options = (rawOptions ?? []) as unknown[];
    options.forEach((option, optionIndex) => {
      if (typeof option !== 'string') {
        parseError(`${path}.attributes.options[${optionIndex}] must be a string.`);
      }
    });
    return {
      uid: createUid('field'),
      type,
      id,
      attributes: {
        label: editableString(attributes, 'label', `${path}.attributes`),
        description: editableString(attributes, 'description', `${path}.attributes`),
        options: options as string[],
        multiple: optionalBoolean(attributes, 'multiple', `${path}.attributes`),
      },
      validations: parseValidations(field, path),
    };
  }

  const rawOptions = attributes.options;
  if (rawOptions !== undefined && !Array.isArray(rawOptions)) {
    parseError(`${path}.attributes.options must be a list of checkbox options.`);
  }
  return {
    uid: createUid('field'),
    type,
    id,
    attributes: {
      label: editableString(attributes, 'label', `${path}.attributes`),
      description: editableString(attributes, 'description', `${path}.attributes`),
      options: ((rawOptions ?? []) as unknown[]).map((option, optionIndex) =>
        parseCheckboxOption(option, `${path}.attributes.options[${optionIndex}]`),
      ),
    },
  };
}

function checkStructureLimits(root: unknown): void {
  let nodes = 0;
  const visit = (value: unknown, depth: number): void => {
    nodes += 1;
    if (nodes > MAX_STRUCTURE_NODES) parseError('YAML contains too many values.');
    if (depth > MAX_STRUCTURE_DEPTH) parseError('YAML structure is too deeply nested.');
    if (Array.isArray(value)) {
      value.forEach((entry) => visit(entry, depth + 1));
      return;
    }
    if (isRecord(value)) {
      for (const [key, entry] of Object.entries(value)) {
        if (key === '__proto__' || key === 'prototype' || key === 'constructor') {
          parseError(`Unsafe mapping key "${key}" is not allowed.`);
        }
        visit(entry, depth + 1);
      }
    }
  };
  visit(root, 0);
}

function yamlError(document: YamlDocument): IssueFormParseError | undefined {
  const first = document.errors[0];
  if (!first) return undefined;
  const details = first as unknown as {
    message?: string;
    linePos?: Array<{ line: number; col: number }>;
  };
  const position = details.linePos?.[0];
  const message = details.message ?? 'Unable to parse YAML.';
  if (!position) return new IssueFormParseError(message);
  return new IssueFormParseError(message, position.line, position.col);
}

function parseOptions(
  filenameOrOptions: string | ParseIssueFormOptions | undefined,
): Required<ParseIssueFormOptions> {
  if (typeof filenameOrOptions === 'string') {
    return { filename: filenameOrOptions, maxBytes: DEFAULT_MAX_YAML_BYTES };
  }
  return {
    filename: filenameOrOptions?.filename ?? 'imported-form.yml',
    maxBytes: filenameOrOptions?.maxBytes ?? DEFAULT_MAX_YAML_BYTES,
  };
}

export function parseIssueFormYaml(
  source: string,
  filenameOrOptions?: string | ParseIssueFormOptions,
): IssueForm {
  const options = parseOptions(filenameOrOptions);
  if (typeof source !== 'string') throw new IssueFormParseError('YAML source must be text.');
  if (new TextEncoder().encode(source).byteLength > options.maxBytes) {
    throw new IssueFormParseError(
      `YAML file is too large (maximum ${options.maxBytes.toLocaleString('en-US')} bytes).`,
    );
  }

  let document: YamlDocument;
  try {
    document = parseDocument(source, {
      schema: 'core',
      prettyErrors: true,
      strict: true,
      uniqueKeys: true,
      merge: false,
    });
  } catch (error) {
    throw new IssueFormParseError(error instanceof Error ? error.message : 'Unable to parse YAML.');
  }

  const syntaxError = yamlError(document);
  if (syntaxError) throw syntaxError;

  let parsed: unknown;
  try {
    parsed = document.toJS({ maxAliasCount: 0 });
  } catch (error) {
    throw new IssueFormParseError(
      error instanceof Error ? `Unsafe YAML alias: ${error.message}` : 'Unsafe YAML alias.',
    );
  }
  checkStructureLimits(parsed);
  const root = expectRecord(parsed, 'Issue form');
  const rawBody = root.body;
  if (!Array.isArray(rawBody)) parseError('Issue form.body must be a list.');

  return {
    uid: createUid('form'),
    filename: options.filename,
    name: requiredString(root, 'name', 'Issue form'),
    description: requiredString(root, 'description', 'Issue form'),
    title: editableString(root, 'title', 'Issue form'),
    labels: optionalStringList(root, 'labels', 'Issue form'),
    assignees: optionalStringList(root, 'assignees', 'Issue form'),
    body: rawBody.map((value, index) => parseField(value, index)),
  };
}

function optionalProperty(target: UnknownRecord, key: string, value: string): void {
  if (value !== '') target[key] = value;
}

function serializeField(field: IssueField): UnknownRecord {
  if (field.type === 'markdown') {
    return {
      type: field.type,
      attributes: { value: field.attributes.value },
    };
  }

  if (field.type === 'input') {
    const attributes: UnknownRecord = { label: field.attributes.label };
    optionalProperty(attributes, 'description', field.attributes.description);
    optionalProperty(attributes, 'placeholder', field.attributes.placeholder);
    optionalProperty(attributes, 'value', field.attributes.value);
    return {
      type: field.type,
      id: field.id,
      attributes,
      validations: { required: field.validations.required },
    };
  }

  if (field.type === 'textarea') {
    const attributes: UnknownRecord = { label: field.attributes.label };
    optionalProperty(attributes, 'description', field.attributes.description);
    optionalProperty(attributes, 'placeholder', field.attributes.placeholder);
    optionalProperty(attributes, 'value', field.attributes.value);
    optionalProperty(attributes, 'render', field.attributes.render);
    return {
      type: field.type,
      id: field.id,
      attributes,
      validations: { required: field.validations.required },
    };
  }

  if (field.type === 'dropdown') {
    const attributes: UnknownRecord = { label: field.attributes.label };
    optionalProperty(attributes, 'description', field.attributes.description);
    attributes.options = [...field.attributes.options];
    attributes.multiple = field.attributes.multiple;
    return {
      type: field.type,
      id: field.id,
      attributes,
      validations: { required: field.validations.required },
    };
  }

  const attributes: UnknownRecord = { label: field.attributes.label };
  optionalProperty(attributes, 'description', field.attributes.description);
  attributes.options = field.attributes.options.map(({ label, required }) => ({ label, required }));
  return {
    type: field.type,
    id: field.id,
    attributes,
  };
}

export function issueFormToYamlObject(form: IssueForm): UnknownRecord {
  return {
    name: form.name,
    description: form.description,
    title: form.title,
    labels: normalizeStringList(form.labels),
    assignees: normalizeStringList(form.assignees),
    body: form.body.map(serializeField),
  };
}

export function serializeIssueForm(form: IssueForm): string {
  return stringify(issueFormToYamlObject(form), {
    blockQuote: 'literal',
    lineWidth: 0,
  });
}

export const serializeIssueFormYaml = serializeIssueForm;
export const parseIssueForm = parseIssueFormYaml;

export type ParseIssueFormResult =
  { ok: true; form: IssueForm } | { ok: false; error: IssueFormParseError };

export function tryParseIssueFormYaml(
  source: string,
  filenameOrOptions?: string | ParseIssueFormOptions,
): ParseIssueFormResult {
  try {
    return { ok: true, form: parseIssueFormYaml(source, filenameOrOptions) };
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof IssueFormParseError
          ? error
          : new IssueFormParseError(
              error instanceof Error ? error.message : 'Unable to parse YAML.',
            ),
    };
  }
}

export function serializeRepositoryConfig(settings: RepositorySettings): string {
  return stringify(
    {
      blank_issues_enabled: settings.blankIssuesEnabled,
      contact_links: settings.contactLinks.map(({ name, url, about }) => ({
        name: name.trim(),
        url: url.trim(),
        about: about.trim(),
      })),
    },
    { blockQuote: 'literal', lineWidth: 0 },
  );
}
